<!--
  Port of vendor-ref/mara/app/ui/src/components/chat/ChatInput.vue in its workspace form ('p-4 pt-0 -mt-5'): the
  example prompt buttons above the rounded box (prod shows its examples the same way on a new chat), the
  "How can MARA help?" field, Attach file / Draw SMILES / Run tool / Run workflow and the send button. The examples
  are Lite's three baked prompts; the four tool buttons open the shared nudge, since they need a Nanome account.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import { BAKED_PROMPTS } from '@/mara/baked';
import { useMaraChat } from '@/mara/chat';
import TeaserCta from '../TeaserCta.vue';
import MessageInput from './MessageInput.vue';

const chat = useMaraChat();
const messageInput = ref<InstanceType<typeof MessageInput>>();
const teaser = ref<InstanceType<typeof Popover>>();
const message = ref('');

const canSend = computed(() => !chat.busy && !!message.value.trim());

function send() {
  if (!canSend.value) return;
  const text = message.value.trim();
  messageInput.value?.clear();
  void chat.send(text);
}

function runExample(text: string) {
  if (chat.busy) return;
  void chat.send(text);
}

function showTeaser(e: Event) { teaser.value?.toggle(e); }
</script>

<template>
  <!-- prod pulls the box 1.25rem up (-mt-5) over the list's bottom padding; the example row takes that room here -->
  <div class="chat-input w-full p-4 pt-0">
    <div class="mb-2 flex flex-wrap-reverse justify-center gap-2" data-test="chat-examples">
      <Button
        v-for="example in BAKED_PROMPTS"
        :key="example.id"
        :label="example.text"
        :data-test="`chat-example-${example.id}`"
        :disabled="chat.busy"
        class="ws-nowrap bg-ground"
        severity="secondary"
        size="small"
        outlined
        rounded
        @click="runExample(example.text)"
      />
    </div>
    <div class="relative w-full max-w-50rem mx-auto rd-t-10 rd-b-10 b-1 b-solid b-ground bg-content flex flex-col">
      <MessageInput
        ref="messageInput"
        v-model="message"
        placeholder="How can MARA help?"
        :editable="!chat.busy"
        @send="send"
      >
        <Button v-tooltip.top="'Attach file'" icon="i-tabler-paperclip" severity="secondary" rounded @click="showTeaser" />
        <Button v-tooltip.top="'Draw SMILES'" icon="i-tabler-hexagon" severity="secondary" rounded @click="showTeaser" />
        <Button v-tooltip.top="'Run tool'" icon="i-tabler-tool" severity="secondary" rounded @click="showTeaser" />
        <Button v-tooltip.top="'Run workflow'" icon="i-tabler-route" severity="secondary" rounded @click="showTeaser" />

        <template #send>
          <Button
            :disabled="!canSend"
            class="ml-1"
            icon="i-tabler-arrow-up"
            rounded
            aria-label="Send"
            data-test="chat-send"
            @click="send"
          />
        </template>
      </MessageInput>
    </div>

    <Popover ref="teaser">
      <TeaserCta title="MARA's tools run in Nanome" text="Attach files, draw a molecule, or run a tool or a saved workflow on this scene." />
    </Popover>
  </div>
</template>
